// store/slices/ledgerReportSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { getCurrentMonth, getStartOfYear } from '../../Utils/FormatDate';

// ✅ Helper: Date Init from LocalStorage or Fallback
const parseStoredDate = (key, fallback) => {
  const stored = localStorage.getItem(key);
  const parsed = stored ? new Date(stored) : fallback;
  return isNaN(parsed) ? fallback : parsed;
};

const initialStartDate = parseStoredDate('StartDate', getStartOfYear(new Date()));
const initialEndDate = parseStoredDate('EndDate', getCurrentMonth(new Date()));
const initialStartYearDate = new Date(Date.UTC(initialStartDate.getFullYear(), 0, 1));
const initialEndYearDate = new Date(Date.UTC(initialEndDate.getFullYear(), 11, 31));

const initialState = {
  palCodesData: [],
  palDates: [],
  trialBalanceData: null,
  balanceControl: [],
  palcodePlacementViolations: [],
  loading: false,
  endDate: initialEndDate,
  startDate: initialStartDate,
  startYearDate: initialStartYearDate,
  endYearDate: initialEndYearDate,
  lastFetchStartDate: null,
  lastFetchEndDate: null,
};

const ledgerReportSlice = createSlice({
  name: 'ledgerReport',
  initialState,
  reducers: {
    resetLedgerReport: (state) => ({
      ...initialState,
      // Preserve user-selected dates which are persisted in localStorage
      startDate: state.startDate,
      endDate: state.endDate,
      startYearDate: state.startYearDate,
      endYearDate: state.endYearDate,
    }),
    setPalCodesData: (state, action) => {
      const { palCodesData } = action.payload || {};
      if (Array.isArray(palCodesData)) {
        state.palCodesData = palCodesData;
      }
    },
    setPalDates: (state, action) => {
      state.palDates = action.payload;
    },
    setBalanceControl: (state, action) => {
      state.balanceControl = action.payload;
    },
    setPalcodePlacementViolations: (state, action) => {
      state.palcodePlacementViolations = action.payload;
    },
    setTrialBalanceData: (state, action) => {
      state.trialBalanceData = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setEndDate: (state, action) => {
      const newEndDate = action.payload;
      state.endDate = newEndDate;
      if (newEndDate instanceof Date && !isNaN(newEndDate)) {
        localStorage.setItem('EndDate', newEndDate.toISOString());
      }
    },
    setStartDate: (state, action) => {
      const newStartDate = action.payload;
      state.startDate = newStartDate;
      if (newStartDate instanceof Date && !isNaN(newStartDate)) {
        localStorage.setItem('StartDate', newStartDate.toISOString());
      }
    },
    setStartYearDate: (state, action) => {
      const newStartYearDate = action.payload;
      state.startYearDate = newStartYearDate;
      if (newStartYearDate instanceof Date && !isNaN(newStartYearDate)) {
        localStorage.setItem('StartYearDate', newStartYearDate.toISOString());
      }
    },
    setEndYearDate: (state, action) => {
      const newEndYearDate = action.payload;
      state.endYearDate = newEndYearDate;
      if (newEndYearDate instanceof Date && !isNaN(newEndYearDate)) {
        localStorage.setItem('EndYearDate', newEndYearDate.toISOString());
      }
    },
    setLastFetchDates: (state, action) => {
      state.lastFetchStartDate = action.payload.startDate;
      state.lastFetchEndDate = action.payload.endDate;
    },
    
  }
});

export const {
  resetLedgerReport,
  setPalCodesData,
  setPalDates,
  setBalanceControl,
  setPalcodePlacementViolations,
  setTrialBalanceData,
  setLoading,
  setEndDate,
  setStartDate,
  setStartYearDate,
  setEndYearDate,
  setLastFetchDates
} = ledgerReportSlice.actions;

export default ledgerReportSlice.reducer;
